import { Overlay, OverlayRef, ScrollStrategyOptions } from "@angular/cdk/overlay"
import { TemplatePortal } from "@angular/cdk/portal"
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  inject,
} from "@angular/core"
import { IconImageService } from "@shared/services/icon-image.service"

// matches the max-height transition in flow-drop-down.component.scss
const CLOSE_TRANSITION_MS = 250

@Component({
  selector: "flow-drop-down",
  templateUrl: "./flow-drop-down.component.html",
  styleUrl: "./flow-drop-down.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlowDropDownComponent implements OnDestroy {
  public readonly iconService = inject(IconImageService)
  private readonly elementRef = inject(ElementRef)
  private readonly overlay = inject(Overlay)
  private readonly scrollStrategies = inject(ScrollStrategyOptions)
  private readonly viewContainerRef = inject(ViewContainerRef)
  private readonly changeDetectorRef = inject(ChangeDetectorRef)

  @ViewChild("dropDownContent", { static: true }) private dropDownContent!: TemplateRef<unknown>

  @Input() public type: DropDownType = "item"
  @Input() public searchbarLabel: string = ""
  @Input() public footer: DropDownFooter | null = null
  @Input() public dividers: DropDownDivider[] = []
  @Input() public items: DropDownItem[] = []
  @Input() public width: number = 250
  @Input() public height: number = 360
  /** Lets the user append custom items via a footer trigger and an inline add row. */
  @Input() public addable: boolean = false
  @Input() public addPlaceholder: string = ""
  /** Label for the footer trigger that reveals the add row. */
  @Input() public addTriggerLabel: string = "Add item"
  /** Checkbox type for added items. Defaults to matching the existing items. */
  @Input() public addedCheckbox: CheckboxType | null = null
  /** Shown when the list has no items at all. */
  @Input() public emptyTitle: string = "Nothing here yet"
  @Input() public emptySubtitle: string = ""
  /** Shown when a search term filters every item away. */
  @Input() public noResultsTitle: string = "No search results"
  @Input() public noResultsSubtitle: string = "We couldn't find what you're looking for"
  @Input() public anchored: boolean = true
  @Input() public foldUp: boolean = false
  @Input() public alignRight: boolean = false
  @Input() public triggerElement: HTMLElement | null = null
  /** Sizes the panel to the trigger instead of the `width` input. */
  @Input() public matchTriggerWidth: boolean = true
  @Input() public inBottomSheet: boolean = false

  public addValue: string = ""
  public addRowOpen: boolean = false

  @Input()
  public get open(): boolean {
    return this._open
  }
  public set open(value: boolean) {
    if (value === this._open) return
    this._open = value
    if (value) {
      this.attachOverlay()
      // the panel animates from max-height 0, so reposition once it has real
      // height or the CDK would pick a position based on a zero-sized panel
      this.reserveHeightForPositioning()
      this.enableScrollAfterOpen()
    } else {
      this.scrollable = false
      this.searchTerm = ""
      this.closeAddRow()
      this.scheduleOverlayTeardown()
    }
  }
  private _open: boolean = false

  private overlayRef: OverlayRef | null = null
  private teardownTimer: ReturnType<typeof setTimeout> | undefined
  private scrollTimer: ReturnType<typeof setTimeout> | undefined
  private scrollAncestors: EventTarget[] = []

  public get useOverlay(): boolean {
    return this.anchored && !!this.triggerElement
  }

  public scrollable: boolean = false
  public searchTerm: string = ""

  @Output() public openChange = new EventEmitter<boolean>()
  @Output() public footerTertiaryAction = new EventEmitter<boolean>()
  @Output() public footerSecondaryAction = new EventEmitter<boolean>()
  @Output() public footerPrimaryAction = new EventEmitter<boolean>()
  @Output() public itemSelected = new EventEmitter<number>()
  @Output() public itemsChange = new EventEmitter<DropDownItem[]>()
  @Output() public itemAdded = new EventEmitter<DropDownItem>()
  @Output() public itemRemoved = new EventEmitter<DropDownItem>()

  @HostListener("document:click", ["$event"])
  public onDocumentClick(event: MouseEvent): void {
    if (!this._open) return
    const target = event.target as Node
    if (this.elementRef.nativeElement.contains(target)) return
    if (this.overlayRef?.overlayElement.contains(target)) return
    if (this.triggerElement?.contains(target)) return
    this._open = false
    this.scrollable = false
    this.searchTerm = ""
    this.closeAddRow()
    this.changeDetectorRef.markForCheck()
    this.openChange.emit(false)
  }

  public get filteredItems(): DropDownItem[] {
    const term = this.searchTerm.trim().toLowerCase()
    if (!term) return this.items
    return this.items.filter((item) => this.matchesSearch(item, term))
  }

  public get rows(): DropDownRow[] {
    const term = this.searchTerm.trim().toLowerCase()
    const dividers = this.allDividers()
    const rows: DropDownRow[] = []

    this.items.forEach((item, index) => {
      const divider = dividers.find((d) => d.index === index)
      if (divider && this.groupHasVisibleItem(divider, dividers, term)) {
        rows.push({ kind: "divider", divider })
      }
      if (!term || this.matchesSearch(item, term)) {
        rows.push({ kind: "item", item })
      }
    })

    return rows
  }

  private allDividers(): DropDownDivider[] {
    const dividers = [...this.dividers]
    const firstCustomIndex = this.items.findIndex((item) => item.custom)
    if (firstCustomIndex !== -1) {
      const customDivider = new DropDownDivider()
      customDivider.title = "Custom"
      customDivider.index = firstCustomIndex
      dividers.push(customDivider)
    }
    return dividers.sort((a, b) => a.index - b.index)
  }

  public get hasVisibleRows(): boolean {
    return this.rows.some((row) => row.kind === "item")
  }

  private groupHasVisibleItem(divider: DropDownDivider, dividers: DropDownDivider[], term: string): boolean {
    const next = dividers.find((d) => d.index > divider.index)
    const end = next ? next.index : this.items.length
    for (let i = divider.index; i < end; i++) {
      const item = this.items[i]
      if (item && (!term || this.matchesSearch(item, term))) return true
    }
    return false
  }

  private matchesSearch(item: DropDownItem, term: string): boolean {
    if (item.title.toLowerCase().includes(term)) return true
    if (item.subtitle.toLowerCase().includes(term)) return true
    if (this.type === "phone-number" && item.countryCode.toLowerCase().includes(term)) return true
    return false
  }

  public onTransitionEnd(event: TransitionEvent) {
    if (event.propertyName !== "max-height") return
    this.scrollable = this._open
    if (!this._open) this.detachOverlay()
  }

  private attachOverlay(): void {
    if (!this.useOverlay || this.overlayRef) return
    this.overlayRef = this.overlay.create({
      positionStrategy: this.buildPositionStrategy(),
      scrollStrategy: this.scrollStrategies.reposition({ autoClose: false }),
      width: this.matchTriggerWidth ? this.triggerElement!.offsetWidth : this.width,
      maxHeight: this.height,
    })
    this.overlayRef.attach(new TemplatePortal(this.dropDownContent, this.viewContainerRef))
    this.trackAncestorScroll()
  }

  // the CDK's reposition strategy only follows containers registered with its
  // ScrollDispatcher via cdkScrollable, so a plain overflow:auto ancestor — a dialog
  // body, say — would scroll the trigger away while the panel stayed put. follow those
  // ancestors directly so the panel stays anchored wherever it is used
  private trackAncestorScroll(): void {
    this.untrackAncestorScroll()
    this.scrollAncestors = this.findScrollAncestors()
    this.scrollAncestors.forEach((ancestor) => ancestor.addEventListener("scroll", this.onAncestorScroll, { passive: true }))
  }

  private untrackAncestorScroll(): void {
    this.scrollAncestors.forEach((ancestor) => ancestor.removeEventListener("scroll", this.onAncestorScroll))
    this.scrollAncestors = []
  }

  private readonly onAncestorScroll = (): void => {
    this.overlayRef?.updatePosition()
  }

  private findScrollAncestors(): EventTarget[] {
    const ancestors: EventTarget[] = []
    let node = (this.triggerElement ?? this.elementRef.nativeElement)?.parentElement as HTMLElement | null

    while (node && node !== document.body) {
      const style = getComputedStyle(node)
      if (/(auto|scroll|overlay)/.test(style.overflowY + style.overflowX)) ancestors.push(node)
      node = node.parentElement
    }

    return ancestors
  }

  // the panel animates up from max-height 0, so its first measurement is zero-height
  // and would bias the CDK toward the "below" position; reposition once it has size
  private reserveHeightForPositioning(): void {
    if (!this.overlayRef) return
    this.overlayRef.updatePosition()
    requestAnimationFrame(() => this.overlayRef?.updatePosition())
  }

  // transitionend drives this too, but it never fires when the panel opens without
  // animating, which would leave the list stuck at overflow-y: hidden
  private enableScrollAfterOpen(): void {
    clearTimeout(this.scrollTimer)
    this.scrollTimer = setTimeout(() => {
      if (!this._open) return
      this.scrollable = true
      this.changeDetectorRef.markForCheck()
    }, CLOSE_TRANSITION_MS)
  }

  // transitionend is the normal teardown trigger, but it never fires if the panel
  // is closed while hidden, so this backstop guarantees the overlay is disposed
  private scheduleOverlayTeardown(): void {
    if (!this.overlayRef) return
    clearTimeout(this.teardownTimer)
    this.teardownTimer = setTimeout(() => {
      if (!this._open) this.detachOverlay()
    }, CLOSE_TRANSITION_MS)
  }

  private detachOverlay(): void {
    clearTimeout(this.teardownTimer)
    clearTimeout(this.scrollTimer)
    this.untrackAncestorScroll()
    this.overlayRef?.dispose()
    this.overlayRef = null
  }

  // ordered preference: the CDK picks the first position that fits, flipping up
  // or switching side alignment automatically instead of relying on foldUp/alignRight
  private buildPositionStrategy() {
    const below = { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top", offsetY: 4 } as const
    const above = { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom", offsetY: -4 } as const
    const belowRight = { originX: "end", originY: "bottom", overlayX: "end", overlayY: "top", offsetY: 4 } as const
    const aboveRight = { originX: "end", originY: "top", overlayX: "end", overlayY: "bottom", offsetY: -4 } as const

    const preferred = this.alignRight ? [belowRight, aboveRight, below, above] : [below, above, belowRight, aboveRight]
    const positions = this.foldUp ? [preferred[1], preferred[0], preferred[3], preferred[2]] : preferred

    return this.overlay
      .position()
      .flexibleConnectedTo(this.triggerElement!)
      .withPositions(positions)
      .withPush(false)
      .withViewportMargin(8)
      .withFlexibleDimensions(false)
  }

  public ngOnDestroy(): void {
    this.detachOverlay()
  }

  public onSearchInputChange(value: string) {
    this.searchTerm = value
  }

  public get deferSelection(): boolean {
    return !!this.footer || this.inBottomSheet
  }

  public onItemSelected(item: DropDownItem) {
    if (item.disabled) return
    if (item.checkbox === "radio" || !this.showsControl(item)) {
      this.selectSingle(item)
      if (this.deferSelection) return
      this.itemSelected.emit(this.items.indexOf(item))
      return
    }
    if (item.checkbox === "checkbox") item.selected = !item.selected
    this.itemSelected.emit(this.items.indexOf(item))
  }

  private showsControl(item: DropDownItem): boolean {
    return this.type === "item" && item.checkbox !== "none"
  }

  private selectSingle(item: DropDownItem) {
    this.items.forEach((i) => (i.selected = i === item))
  }

  public get canAddCustomItem(): boolean {
    return this.addValue.trim().length > 0
  }

  public onAddValueChange(value: string) {
    this.addValue = value
  }

  public showAddRow(): void {
    this.addRowOpen = true
  }

  public addCustomItem() {
    const title = this.addValue.trim()
    if (!title) return

    // the value is already on the list, so tick that one instead of adding a second row
    const existing = this.items.find((option) => option.title.toLowerCase() === title.toLowerCase())
    if (existing) {
      if (existing.checkbox === "checkbox") existing.selected = true
      this.addValue = ""
      this.itemAdded.emit(existing)
      return
    }

    const item = new DropDownItem()
    item.title = title
    item.custom = true
    item.checkbox = this.customCheckboxType()
    this.items = [...this.items, item]
    this.addValue = ""
    this.itemAdded.emit(item)
    this.itemsChange.emit(this.items)
  }

  public deleteCustomItem(item: DropDownItem) {
    this.items = this.items.filter((i) => i !== item)
    this.itemRemoved.emit(item)
    this.itemsChange.emit(this.items)
  }

  /** Closes the add row and discards whatever was typed. */
  public closeAddRow() {
    this.addValue = ""
    this.addRowOpen = false
  }

  // prefer a built-in item as the reference, but fall back to any existing item so a
  // list made up entirely of custom entries still gets checkboxes rather than "none"
  private customCheckboxType(): CheckboxType {
    if (this.addedCheckbox) return this.addedCheckbox
    const reference = this.items.find((item) => !item.custom) ?? this.items[0]
    return reference ? reference.checkbox : "none"
  }

  footerClick(action: "tertiary" | "secondary" | "primary") {
    switch (action) {
      case "tertiary":
        this.footerTertiaryAction.emit(true)
        break
      case "secondary":
        this.footerSecondaryAction.emit(true)
        break
      case "primary":
        this.emitPendingSelection()
        this.footerPrimaryAction.emit(true)
        break
    }
  }

  private emitPendingSelection() {
    if (!this.deferSelection) return
    const index = this.items.findIndex((i) => i.selected && (i.checkbox === "radio" || !this.showsControl(i)))
    if (index !== -1) this.itemSelected.emit(index)
  }
}
export type DropDownType = "item" | "phone-number" | "action" | "avatar"
export class DropDownFooter {
  tertiaryText: string = "Reset"
  secondaryText: string = "Cancel"
  primaryText: string = "Confirm"
}
export class DropDownDivider {
  title: string = ""
  index: number = 0
}
export type DropDownRow = { kind: "item"; item: DropDownItem } | { kind: "divider"; divider: DropDownDivider }
export class DropDownItem {
  selected: boolean = false //If there is inital value set this to true
  title: string = ""
  subtitle: string = ""
  avatar: string = ""
  icon: string = ""
  countryCode: string = ""
  checkbox: CheckboxType = "none"
  destructive: boolean = false
  custom: boolean = false
  disabled: boolean = false
  /** Shown as a tooltip on hover while the item is disabled. */
  disabledReason: string = ""
}
export type CheckboxType = "checkbox" | "radio" | "none"
